import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Settings2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const DEFAULT_VOUCHER_CATEGORIES = ['Supplies', 'Training', 'Software', 'Contractor', 'Rent', 'Utilities', 'Other'];

export const DEFAULT_VOUCHER_METHODS = ['Bank Transfer', 'Cash', 'Cheque', 'Online Banking'];

export const VoucherCategoryManager = ({ categories, onChange, onRename, label = 'Category', title = 'Voucher Categories', testid = 'category' }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');

  const exists = (name, skip) => categories.some((c) => c !== skip && c.toLowerCase() === name.toLowerCase());

  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (exists(name)) { toast.error(`${label} already exists`); return; }
    onChange([...categories, name]);
    setDraft('');
    toast.success(`Added "${name}"`);
  };

  const startEdit = (c) => { setEditing(c); setEditValue(c); };

  const commitEdit = () => {
    const name = editValue.trim();
    if (!name || name === editing) { setEditing(null); return; }
    if (exists(name, editing)) { toast.error(`${label} already exists`); return; }
    onChange(categories.map((c) => (c === editing ? name : c)));
    onRename?.(editing, name);
    setEditing(null);
    toast.success(`${label} renamed`);
  };

  const remove = (c) => {
    if (categories.length === 1) { toast.error(`Keep at least one ${label.toLowerCase()}`); return; }
    onChange(categories.filter((x) => x !== c));
    toast.success(`Removed "${c}"`);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700" data-testid={`manage-${testid}-btn`}>
        <Settings2 className="h-3.5 w-3.5" /> Manage
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-sm" data-testid={`${testid}-manager-dialog`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder={`New ${label.toLowerCase()} name`} data-testid={`new-${testid}-input`} />
            <Button type="button" onClick={add} className="bg-orange-500 hover:bg-orange-600" data-testid={`add-${testid}-btn`}><Plus className="h-4 w-4" /></Button>
          </div>
          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border" data-testid={`${testid}-list`}>
            {categories.map((c) => (
              <li key={c} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`${testid}-item-${c}`}>
                {editing === c ? (
                  <>
                    <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && commitEdit()} className="h-8" autoFocus data-testid={`edit-${testid}-input`} />
                    <button type="button" onClick={commitEdit} className="rounded p-1 text-emerald-600 hover:bg-emerald-50" data-testid={`confirm-rename-${testid}-btn`}><Check className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setEditing(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-slate-700">{c}</span>
                    <button type="button" title="Rename" onClick={() => startEdit(c)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-blue-600" data-testid={`rename-${testid}-${c}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" title="Delete" onClick={() => remove(c)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-${testid}-${c}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} data-testid={`close-${testid}-manager`}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
